import React, { ReactNode, useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  SortingState,
  TableMeta,
} from '@tanstack/react-table';
import { BaseTableMobileCarousel } from './BaseTableMobile/BaseTableMobileCarousel';
import { BaseTableMobileCard } from './BaseTableMobile/BaseTableMobileCard';
import { BaseIconButton } from '../Button';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useSearchByHeader } from './useSearchByHeader';
import { usePagination } from './usePagination';
import { SearchByHeaderPanel } from './SearchByHeaderPanel';
import { cn } from '@/lib/utils';
import { BaseCard } from '../Cards/BaseCard';
import { useOverflow } from '@/hooks/shared/useOverflow';
import { HighlightJSX } from '../Highlight';
import { useInfiniteScroll } from '@/hooks/shared/useIntiniteScroll';
import { useMergedRefs } from '@/hooks/shared/useMergedRefs';
import { ChevronLeftIcon, ChevronRightIcon, NoResults } from '@/assets/Icons';
import { usePortal } from '@/hooks/shared';
import { PORTAL_TARGETS } from '@/types/shared/navigation-tabs.enums';
import { Icon } from '@/components/shared/display/Icon';
import { InputVariant } from '../Inputs';
import { CopyButton } from './CopyButton';

// 💡 Bidirectional drag scroll hook
const useDragScroll = <T extends HTMLElement>(): React.RefObject<T> => {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.pageX - container.offsetLeft;
      startY = e.pageY - container.offsetTop;
      scrollLeft = container.scrollLeft;
      scrollTop = container.scrollTop;
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const y = e.pageY - container.offsetTop;
      container.scrollLeft = scrollLeft - (x - startX);
      container.scrollTop = scrollTop - (y - startY);
    };

    const stopDragging = () => {
      isDragging = false;
      container.style.cursor = 'grab';
      container.style.removeProperty('user-select');
    };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseleave', stopDragging);
    container.addEventListener('mouseup', stopDragging);

    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', stopDragging);
      container.removeEventListener('mouseup', stopDragging);
    };
  }, []);

  return containerRef;
};

const tableRowStyle =
  'group whitespace-nowrap max-w-screen truncate hover:border-2 hover:cursor-pointer hover:rounded-lg ';
const tableCellStyle = `text-sm font-medium first-of-type:text-left bg-white dark:bg-[#52606d]  py-2 px-6 border-gray-400 h-8 first-of-type:w-[250px] max-w-fit min-w-max`;
const overflowBorder = 'first-of-type:border-y-0 first-of-type:border first-of-type:border-l-0 border-t-0 border-b-0';
const tableHeader =
  'bg-gray-200 dark:bg-gray-700 sticky top-0 z-20 px-4 first-of-type:text-left first-of-type:z-[22] text-sm font-normal align-bottom';
const stickyFirstColumnClass = 'sticky left-0 z-10';

// Memoized Table Row Component
interface TableRowProps<T> {
  row: any;
  rowNum: number;
  totalRows: number;
  variant: string;
  classNames?: any;
  fnClassNames?: any;
  rowClickHandler?: (row: any) => void;
  renderEditRow?: (row: any) => ReactNode;
  enableEditing?: boolean;
  searchValue?: string;
  stickyFirstColumn?: boolean;
  isOverflowing?: boolean;
  hoveredColumnIndex?: number | null;
  enableColumnHover?: boolean;
  columnHoverClassName?: string;
  selectedRowId?: string | null;
  getRowId?: (row: any) => string;
}

const TableRow = memo(
  <T,>({
    row,
    rowNum,
    totalRows,
    variant,
    classNames,
    fnClassNames,
    rowClickHandler,
    renderEditRow,
    enableEditing,
    searchValue,
    stickyFirstColumn,
    isOverflowing,
    hoveredColumnIndex,
    enableColumnHover = false,
    columnHoverClassName = 'bg-[#F1F1FE] dark:bg-[#4a4d6a]',
    selectedRowId,
    getRowId,
  }: TableRowProps<T>) => {
    const isFirstRow = rowNum === 0;
    const isLastRow = rowNum === totalRows - 1;

    const handleRowClick = useCallback(() => {
      rowClickHandler?.(row.original);
    }, [row.original, rowClickHandler]);

    const currentRowId = getRowId ? getRowId(row.original) : row.original?.id;
    const isSelected = selectedRowId !== undefined && selectedRowId !== null && selectedRowId === currentRowId;

    return (
      <tr
        role="row"
        className={cn(tableRowStyle, classNames?.table?.tr, 'relative group', isSelected && 'bg-primary-50')}
        onClick={handleRowClick}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleRowClick();
          }
        }}
        data-testid={(row.original as any).test_id || `row_${row.id}`}
      >
        {row.getVisibleCells().map((cell: any, index: number) => {
          const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
          const isHoveredColumn = enableColumnHover && hoveredColumnIndex === index;
          const allowCopy = (cell.column.columnDef.meta as any)?.allowCopy;
          const cellValue = cell.getValue();
          const copyText = typeof cellValue === 'string' ? cellValue : String(cellValue ?? '');
          return (
            <td
              key={cell.id}
              role="cell"
              className={cn(
                `${
                  index === 0
                    ? `${tableCellStyle} ${stickyFirstColumn ? stickyFirstColumnClass : ''} ${
                        isOverflowing && stickyFirstColumn ? overflowBorder : ''
                      }`
                    : tableCellStyle
                }`,
                `${
                  variant === 'dense' &&
                  (totalRows === 1
                    ? 'first-of-type:rounded-l-xl last-of-type:rounded-r-xl'
                    : isFirstRow
                    ? 'first-of-type:rounded-tl-xl last-of-type:rounded-tr-xl'
                    : isLastRow && 'first-of-type:rounded-bl-xl last-of-type:rounded-br-xl')
                }`,
                `${variant === 'rounded' && 'first-of-type:rounded-l-xl last-of-type:rounded-r-xl'}`,
                `${variant === 'base' && 'border-b-[0.5px] py-2'}`,
                `${classNames?.table?.td}`,
                `${fnClassNames?.table?.td?.(row.original) || ''}`,
                isHoveredColumn ? columnHoverClassName : '',
                isSelected && 'bg-primary-50'
              )}
              style={{
                width: cell.column.getSize() ? `${cell.column.getSize()}px` : 'auto',
                minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                maxWidth: cell.column.columnDef.maxSize ? `${cell.column.columnDef.maxSize}px` : undefined,
              }}
            >
              {allowCopy && copyText ? (
                <div className="inline-flex items-center gap-1">
                  <HighlightJSX searchTerm={searchValue ?? ''}>{cellContent}</HighlightJSX>
                  <CopyButton text={copyText} />
                </div>
              ) : (
                <HighlightJSX searchTerm={searchValue ?? ''}>{cellContent}</HighlightJSX>
              )}
            </td>
          );
        })}
        {renderEditRow && enableEditing && (
          <td className="sticky right-0 top-0 group-hover:bg-gray-100 dark:group-hover:bg-gray-600">
            {renderEditRow(row)}
          </td>
        )}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

export interface BaseTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  rowClickHandler?: (row: any) => void;
  renderEditRow?: (row: any) => ReactNode;
  mobileVariant?: 'card' | 'carousel' | 'none';
  variant?: 'dense' | 'spaced' | 'rounded' | 'base';
  pageSize?: number;
  enableSorting?: boolean;
  enableSearch?: boolean;
  enableSearchDropdown?: boolean;
  searchPlaceholder?: string;
  enableEditing?: boolean;
  hideHeaders?: boolean;
  wrapHeaders?: boolean;
  hasMoreData?: boolean;
  stickyFirstColumn?: boolean;
  title?: ReactNode | string;
  defaultSortId?: string;
  onFetchMore?: () => Promise<void>;
  enableVirtualization?: boolean;
  virtualItemHeight?: number;
  overscan?: number;
  enableColumnHover?: boolean;
  columnHoverClassName?: string;
  classNames?: {
    card?: { container?: string; content?: string; title?: string };
    table?: { td?: string; container?: string; header?: string; tr?: string; th?: string; table?: string };
    pagination?: { container?: string; text?: string; icons?: string };
  };
  fnClassNames?: {
    table?: {
      td?: (el: any) => string;
    };
  };
  meta?: TableMeta<any>;
  renderSearchInto?: PORTAL_TARGETS;
  emptyStateMessage?: { icon?: ReactNode; message?: ReactNode };
  inputVariant?: InputVariant;
  enableSearchStateSync?: boolean;
  selectedRowId?: string | null;
  getRowId?: (row: T) => string;
}

const BaseTableComponent = <T extends unknown>({
  data,
  columns,
  rowClickHandler,
  mobileVariant = 'card',
  pageSize,
  enableSorting = true,
  enableSearch = false,
  enableSearchDropdown = true,
  searchPlaceholder,
  hideHeaders = false,
  wrapHeaders = true,
  enableEditing = false,
  stickyFirstColumn = true,
  variant = 'dense',
  title,
  classNames,
  fnClassNames,
  renderEditRow,
  defaultSortId,
  onFetchMore,
  hasMoreData = true,
  enableVirtualization = false,
  virtualItemHeight = 50,
  overscan = 5,
  enableColumnHover = false,
  columnHoverClassName = 'bg-[#F1F1FE] dark:bg-[#4a4d6a]',
  meta,
  renderSearchInto,
  emptyStateMessage,
  inputVariant,
  enableSearchStateSync,
  selectedRowId,
  getRowId,
}: BaseTableProps<T>) => {
  const search = useSearchByHeader(columns, enableSearchStateSync);
  const { pagination, setPagination } = usePagination(pageSize);
  const { isOverflowing, containerRef } = useOverflow();
  const [sorting, setSorting] = useState<SortingState>(defaultSortId ? [{ id: defaultSortId, desc: true }] : []);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredColumnIndex, setHoveredColumnIndex] = useState<number | null>(null);
  const { render } = usePortal(renderSearchInto);

  const defaultEmptyStateMessage = {
    icon: <Icon icon={NoResults} className="mb-2 w-14 h-14" />,
    message: 'No data available',
  };
  const mergedEmptyStateMessage = emptyStateMessage
    ? {
        icon: emptyStateMessage.icon ?? defaultEmptyStateMessage.icon,
        message: emptyStateMessage.message ?? defaultEmptyStateMessage.message,
      }
    : defaultEmptyStateMessage;

  // Memoize table options to prevent unnecessary re-renders
  const tableOptions = useMemo(
    () => ({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
      globalFilterFn: search.filterFn,
      state: {
        pagination,
        sorting,
        globalFilter: search.inputOnlyValue,
      },
      meta: {
        ...meta,
        searchValue: search.inputOnlyValue,
      },
    }),
    [data, columns, pagination, sorting, search.inputOnlyValue, search.filterFn, setPagination, meta]
  );

  const table = useReactTable<T>(tableOptions);

  const fetchMoreData = useCallback(async () => {
    if (isLoading || !hasMoreData || !onFetchMore) return;
    setIsLoading(true);
    await onFetchMore();
    console.log('Fetch more...');
    setIsLoading(false);
  }, [isLoading, hasMoreData, onFetchMore]);

  const scrollRef = useInfiniteScroll(fetchMoreData, 150);
  const dragScrollRef = useDragScroll<HTMLDivElement>();
  const parentRef = useRef<HTMLDivElement>(null);

  // Setup virtualizer for large datasets
  const rows = table.getRowModel().rows;
  const shouldVirtualize = enableVirtualization && rows.length > 50;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => virtualItemHeight, [virtualItemHeight]),
    overscan,
    enabled: shouldVirtualize,
  });

  const virtualItems = shouldVirtualize ? virtualizer.getVirtualItems() : [];

  const mergedRef = useMergedRefs(containerRef, scrollRef, dragScrollRef, parentRef);

  const searchElement = (
    <SearchByHeaderPanel
      {...search}
      showDropdown={enableSearchDropdown}
      customPlaceholder={searchPlaceholder}
      inputVariant={inputVariant}
    />
  );

  const commonTbodyBg = 'bg-white dark:bg-[#52606d]';
  return (
    <>
      <BaseCard
        variant="wrapper"
        title={title}
        classNames={{
          card: cn(
            `max-w-screen rounded-lg p-0 m-0 bg-gray-200 dark:bg-gray-700 max-h-screen shadow-none ${
              mobileVariant === 'none' ? 'block' : 'hidden'
            } lg:block`,
            'rounded-xl',
            classNames?.card?.container
          ),
          content: cn('py-0 bg-inherit overflow-x-auto rounded-lg', classNames?.card?.content),
          title: cn('pb-0 pl-4 pt-3', classNames?.card?.title),
        }}
      >
        {enableSearch && (renderSearchInto ? render(searchElement) : searchElement)}

        <div
          className={cn(
            'overflow-x-auto overflow-y-auto max-h-[80vh] p-0 flex items-start w-full cursor-grab',
            classNames?.table?.container
          )}
          ref={mergedRef}
        >
          <table
            role="table"
            aria-label="Data table"
            className={`table-auto border-separate w-full ${
              variant === 'dense' || variant === 'base' ? 'border-spacing-y-0.5 pb-1' : 'border-spacing-y-2'
            }`}
          >
            {!hideHeaders && (
              <thead role="rowgroup">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} role="row">
                    {headerGroup.headers.map((header, headerIndex) => {
                      const canSort = header.column.getCanSort();
                      const isSorted = header.column.getIsSorted();

                      return (
                        <th
                          key={header.id}
                          role="columnheader"
                          aria-sort={
                            header.column.getIsSorted()
                              ? header.column.getIsSorted() === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                          }
                          tabIndex={canSort ? 0 : -1}
                          onKeyDown={(e) => {
                            if (canSort && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              header.column.toggleSorting();
                            }
                          }}
                          className={cn(
                            `${tableHeader}`,
                            `${variant === 'base' && 'py-0.5'}`,
                            classNames?.table?.th,
                            stickyFirstColumn ? stickyFirstColumnClass : '',
                            isOverflowing && stickyFirstColumn
                              ? `first-of-type:border-r-[0.5px] first-of-type:border-gray-400`
                              : 'border-none',
                            enableColumnHover && hoveredColumnIndex === headerIndex ? columnHoverClassName : ''
                          )}
                          onMouseEnter={enableColumnHover ? () => setHoveredColumnIndex(headerIndex) : undefined}
                          onMouseLeave={enableColumnHover ? () => setHoveredColumnIndex(null) : undefined}
                          style={{
                            width: header.getSize() ? `${header.getSize()}px` : 'auto',
                            minWidth: header.column.columnDef.minSize
                              ? `${header.column.columnDef.minSize}px`
                              : undefined,
                            maxWidth: header.column.columnDef.maxSize
                              ? `${header.column.columnDef.maxSize}px`
                              : undefined,
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={cn(
                                `select-none py-1 px-2 bg-gray-200 dark:bg-gray-700 w-fit flex items-center gap-2 relative mr-2`,
                                `${wrapHeaders ? 'whitespace-nowrap' : 'text-center margin-auto'}`,
                                `${isSorted ? 'bg-white dark:bg-[#52606d] rounded-l-lg' : ''}`,
                                canSort ? 'cursor-pointer' : 'cursor-default'
                              )}
                              onClick={
                                canSort
                                  ? useCallback(
                                      (e: React.MouseEvent) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        requestAnimationFrame(() => {
                                          header.column.getToggleSortingHandler()?.(e);
                                        });
                                      },
                                      [header.column]
                                    )
                                  : undefined
                              }
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}

                              {/* Sorting arrow only if sortable AND sorted */}
                              {canSort && isSorted && (
                                <div className="visible pr-1 absolute right-[-14px] bg-white dark:bg-[#52606d] h-full flex items-center rounded-r-lg">
                                  {{
                                    asc: <ArrowUp size={'16px'} />,
                                    desc: <ArrowDown size={'16px'} />,
                                  }[isSorted as 'asc' | 'desc'] || null}
                                </div>
                              )}
                            </div>
                          )}
                        </th>
                      );
                    })}
                    {renderEditRow && enableEditing && (
                      <th
                        className={cn(
                          'sticky right-0 top-0 ',
                          `${tableHeader}`,
                          classNames?.table?.th,
                          ' z-[1] bg-gray-200 dark:bg-gray-700'
                        )}
                      ></th>
                    )}
                  </tr>
                ))}
              </thead>
            )}
            {rows?.length > 0 ? (
              <tbody
                role="rowgroup"
                className={cn(commonTbodyBg)}
                style={
                  shouldVirtualize
                    ? {
                        height: `${virtualizer.getTotalSize()}px`,
                        position: 'relative',
                      }
                    : undefined
                }
              >
                {shouldVirtualize
                  ? // Virtualized rendering for large datasets
                    virtualItems.map((virtualItem) => {
                      const row = rows[virtualItem.index];
                      return (
                        <div
                          key={row.id}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          <table className="w-full">
                            <tbody>
                              <TableRow
                                row={row}
                                rowNum={virtualItem.index}
                                totalRows={rows.length}
                                variant={variant}
                                classNames={classNames}
                                fnClassNames={fnClassNames}
                                rowClickHandler={rowClickHandler}
                                renderEditRow={renderEditRow}
                                enableEditing={enableEditing}
                                searchValue={search.searchInputRef.current?.value}
                                stickyFirstColumn={stickyFirstColumn}
                                isOverflowing={isOverflowing}
                                hoveredColumnIndex={hoveredColumnIndex}
                                enableColumnHover={enableColumnHover}
                                columnHoverClassName={columnHoverClassName}
                                selectedRowId={selectedRowId}
                                getRowId={getRowId}
                              />
                            </tbody>
                          </table>
                        </div>
                      );
                    })
                  : // Regular rendering for smaller datasets
                    rows.map((row, rowNum) => (
                      <TableRow
                        key={row.id}
                        row={row}
                        rowNum={rowNum}
                        totalRows={rows.length}
                        variant={variant}
                        classNames={classNames}
                        fnClassNames={fnClassNames}
                        rowClickHandler={rowClickHandler}
                        renderEditRow={renderEditRow}
                        enableEditing={enableEditing}
                        searchValue={search.searchInputRef.current?.value}
                        stickyFirstColumn={stickyFirstColumn}
                        isOverflowing={isOverflowing}
                        hoveredColumnIndex={hoveredColumnIndex}
                        enableColumnHover={enableColumnHover}
                        columnHoverClassName={columnHoverClassName}
                        selectedRowId={selectedRowId}
                        getRowId={getRowId}
                      />
                    ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={columns.length} className={cn('rounded-xl text-center h-[752px]', commonTbodyBg)}>
                    <div className=" flex items-center flex-col">
                      {mergedEmptyStateMessage.icon}
                      <div className="text-sm font-semibold">{mergedEmptyStateMessage.message}</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
        {pagination.pageSize < data?.length && (
          <div className={cn('flex items-center justify-center', classNames?.pagination?.container)}>
            <BaseIconButton
              icon={ChevronLeftIcon}
              classNames={{ button: cn('text-primary', classNames?.pagination?.icons) }}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            />
            <div className={classNames?.pagination?.text}>
              {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
            </div>
            <BaseIconButton
              icon={ChevronRightIcon}
              classNames={{ button: cn('text-primary', classNames?.pagination?.icons) }}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            />
          </div>
        )}
      </BaseCard>

      {mobileVariant === 'carousel' ? (
        <BaseTableMobileCarousel table={table} rowClickHandler={rowClickHandler} />
      ) : (
        mobileVariant === 'card' && <BaseTableMobileCard table={table} rowClickHandler={rowClickHandler} />
      )}
    </>
  );
};

export const BaseTable = memo(BaseTableComponent) as typeof BaseTableComponent;
